import { jsx as _jsx } from "react/jsx-runtime";
import { Center, Image } from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { PersonOutline } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { useEffect, useState } from "react";
import { useImageUpload } from "../hooks/useImageUpload";
import { showErrorNotification } from "./Notifications";
export const ImageUpload = ({ size = "7rem", imageUrl = null, imageKey = null, imageVersion = null, imagePrefix = null, fallbackIcon = null, setCompressedImage, }) => {
    const { compressImage } = useImageUpload();
    const [isLoading, setIsLoading] = useState(false);
    const [imageSrc, setImageSrc] = useState(null);
    const [localPreviewSrc, setLocalPreviewSrc] = useState(null);
    // Effect to update imageSrc when existingImageUrl changes
    useEffect(() => {
        if (!imageKey) {
            // If editing an existing record that only has an absolute imageUrl (or if the
            // env var used to build URLs is misconfigured), allow using it directly.
            if (imageUrl) {
                try {
                    const u = new URL(imageUrl);
                    // Cache-bust to avoid sticky cached 404s / old versions.
                    u.searchParams.set("cb", String(Date.now()));
                    setImageSrc(u.toString());
                }
                catch {
                    setImageSrc(imageUrl);
                }
            }
            else {
                setImageSrc(localPreviewSrc);
            }
            return;
        }
        const lowerCasePrefix = imagePrefix ? imagePrefix.toLowerCase() : "";
        const base = String(import.meta.env.VITE_GET_IMAGE_URL || "").replace(/\/+$/, "");
        // Preserve any "/" separators in the key (encode per-segment).
        const normalizedKey = String(imageKey).replace(/^\/+/, "");
        const keyWithPrefix = lowerCasePrefix &&
            !normalizedKey
                .toLowerCase()
                .startsWith(`${lowerCasePrefix.toLowerCase()}/`)
            ? `${lowerCasePrefix}/${normalizedKey}`
            : normalizedKey;
        const path = keyWithPrefix
            .split("/")
            .filter(Boolean)
            .map((seg) => encodeURIComponent(seg))
            .join("/");
        // Build URL safely + keep existing preview until the remote image actually loads.
        let remoteUrl = null;
        try {
            const u = new URL(`${base}/`);
            u.pathname = u.pathname.replace(/\/+$/, "/") + path;
            if (imageVersion)
                u.searchParams.set("versionId", imageVersion);
            // Extra cache-bust for CDNs that might temporarily cache 404s.
            u.searchParams.set("cb", String(Date.now()));
            remoteUrl = u.toString();
        }
        catch {
            remoteUrl = `${base}/${path}${imageVersion ? `?versionId=${imageVersion}` : ""}`;
        }
        if (!remoteUrl)
            return;
        // Preload; if the remote URL isn't ready yet, keep the local preview and retry briefly.
        let cancelled = false;
        const tryLoad = (attempt) => {
            if (cancelled)
                return;
            const img = new window.Image();
            img.onload = () => {
                if (cancelled)
                    return;
                setImageSrc(remoteUrl);
            };
            img.onerror = () => {
                if (cancelled)
                    return;
                if (attempt >= 3)
                    return; // stop retrying; keep whatever we had
                setTimeout(() => tryLoad(attempt + 1), 300 * Math.pow(2, attempt)); // 300ms, 600ms, 1200ms
            };
            img.src = remoteUrl;
        };
        // If we have a local preview, show it until remote loads.
        if (localPreviewSrc)
            setImageSrc(localPreviewSrc);
        tryLoad(0);
        return () => {
            cancelled = true;
        };
    }, [imageKey, imageVersion, imagePrefix, imageUrl, localPreviewSrc]);
    // Cleanup local object URLs to avoid memory leaks
    useEffect(() => {
        return () => {
            if (localPreviewSrc && localPreviewSrc.startsWith("blob:")) {
                URL.revokeObjectURL(localPreviewSrc);
            }
        };
    }, [localPreviewSrc]);
    const handleLoadImage = async (files) => {
        const file = files[0];
        if (!file) {
            showErrorNotification("No file selected");
            return;
        }
        // Create a URL for the image for preview
        const objectUrl = URL.createObjectURL(file);
        setLocalPreviewSrc(objectUrl);
        setImageSrc(objectUrl);
        setIsLoading(true);
        try {
            const compressedImage = await compressImage(file);
            setCompressedImage(compressedImage);
        }
        catch (error) {
            const errorMessage = error instanceof Error
                ? error.message
                : "Failed to update attributes or refresh tokens";
            console.error("Error:", errorMessage);
            showErrorNotification(errorMessage);
            setImageSrc(null); // Reset image preview on error
        }
        finally {
            setIsLoading(false);
        }
    };
    return (_jsx("div", { style: { backgroundColor: "transparent" }, children: _jsx(Dropzone, { style: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "transparent",
                border: `2px ${imageSrc ? "solid" : "dashed"} var(--mantine-color-gray-4)`,
                padding: 0,
                minHeight: size,
                height: size,
                width: size,
                overflow: "hidden",
                cursor: "pointer",
                margin: "auto",
                borderRadius: "50%"
            }, loading: isLoading, onDrop: handleLoadImage, accept: [MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.webp], children: _jsx(Center, { bg: "white", style: { width: "100%", height: "100%", padding: 0 }, children: imageSrc ? (_jsx("div", { style: {
                        height: "100%",
                        width: "100%",
                        borderRadius: "50%",
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                    }, children: _jsx(Image, { src: imageSrc, alt: "Preview", style: {
                            minWidth: "90%",
                            minHeight: "90%"
                        } }) })) : (_jsx(SvgIcon, { component: fallbackIcon || PersonOutline, style: {
                        fontSize: "4rem",
                        color: "var(--mantine-color-gray-6)"
                    } })) }) }) }));
};
